
import { useState, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Message } from "./ChatMessage";
import { submitFeedback } from "./utils/messageUtils";
import { useMessageHandler } from "./hooks/useMessageHandler";
import { useOnlineStatus } from "./hooks/useOnlineStatus";

export const useAssistantChat = () => {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([{
    id: 0,
    role: "assistant",
    content: "Hello! I'm your Hivetivity assistant. How can I help you with the platform today?"
  }]);
  
  const messageIdCounter = useRef<number>(1);
  const { user } = useAuth();
  
  const {
    isLoading,
    networkError,
    retryAttempt,
    lastMessageRef,
    handleSendMessage
  } = useMessageHandler(messages, setMessages, messageIdCounter, user);

  useOnlineStatus(networkError, lastMessageRef.current);

  const handleFeedbackSubmit = async (messageId: number, helpful: boolean) => {
    try {
      const messageToRate = messages.find(msg => msg.id === messageId);
      
      if (!messageToRate || messageToRate.role !== 'assistant') {
        throw new Error('Invalid message to rate');
      }
      
      await submitFeedback(
        messageToRate.content,
        helpful,
        user?.id,
        user?.user_metadata?.role || 'bizadmin'
      );
    } catch (error) {
      console.error("Error submitting feedback:", error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSendMessage(input);
    setInput("");
  };

  const handleRetry = () => {
    if (lastMessageRef.current) {
      handleSubmit(new Event('submit') as any);
    }
  };

  return {
    input,
    setInput,
    messages,
    isLoading,
    networkError,
    retryAttempt,
    handleSendMessage: handleSubmit,
    handleRetry,
    handleFeedbackSubmit
  };
};
